export default function ErrorFallback() {
  // Get error details from console or environment
  const isDev = import.meta.env.DEV
  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-2xl w-full space-y-6">
        {/* Header */}
        <div className="text-center space-y-4 border-b border-red-900 pb-6">
          <div className="text-6xl">⚠️</div>
          <h1 className="text-3xl font-bold text-red-500">Configuration Error</h1>
          <p className="text-zinc-300 text-lg">Cannot initialize Clerk authentication</p>
        </div>

        {/* Main Issue */}
        <div className="bg-red-950 border border-red-800 rounded-lg p-6 space-y-4">
          <h2 className="text-red-400 font-bold text-lg">⚙️ What went wrong?</h2>
          
          <div className="space-y-3 text-sm">
            <div className="bg-red-900/30 rounded p-3">
              <p className="text-red-200 font-mono">Clerk Publishable Key is missing or invalid</p>
            </div>
            
            <p className="text-zinc-300">
              The environment variable <code className="bg-black px-2 py-1 rounded text-red-400">VITE_CLERK_PUBLISHABLE_KEY</code> is not set or has the wrong format.
            </p>
          </div>
        </div>

        {/* Solution */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 space-y-4">
          <h2 className="text-blue-400 font-bold text-lg">✅ How to fix it?</h2>
          
          <div className="space-y-4">
            {/* Step 1 */}
            <div className="border-l-2 border-blue-500 pl-4">
              <p className="text-blue-300 font-semibold mb-2">Step 1: Get your Clerk Publishable Key</p>
              <ol className="text-zinc-400 space-y-1 list-decimal list-inside text-sm">
                <li>
                  Visit{' '}
                  <a 
                    href="https://dashboard.clerk.com/last-active?path=api-keys" 
                    target="_blank" 
                    rel="noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Clerk Dashboard → API Keys
                  </a>
                </li>
                <li>Copy the key that starts with <code className="bg-black px-1 rounded text-blue-300">pk_</code></li>
                <li>Make sure it's the FULL key (not truncated)</li>
              </ol>
            </div>

            {/* Step 2 */}
            <div className="border-l-2 border-blue-500 pl-4">
              <p className="text-blue-300 font-semibold mb-2">Step 2: Add to Cloudflare Pages</p>
              <ol className="text-zinc-400 space-y-1 list-decimal list-inside text-sm">
                <li>Go to your Cloudflare Pages project</li>
                <li>Settings → Environment variables</li>
                <li>
                  Create variable:
                  <div className="bg-black rounded mt-2 p-2 font-mono text-xs text-zinc-300">
                    Name: <span className="text-blue-400">VITE_CLERK_PUBLISHABLE_KEY</span><br/>
                    Value: <span className="text-yellow-400">pk_test_your_full_key_here</span>
                  </div>
                </li>
              </ol>
            </div>

            {/* Step 3 */}
            <div className="border-l-2 border-blue-500 pl-4">
              <p className="text-blue-300 font-semibold mb-2">Step 3: Redeploy</p>
              <ol className="text-zinc-400 space-y-1 list-decimal list-inside text-sm">
                <li>Wait for the deployment to complete</li>
                <li>Hard refresh your browser (Ctrl+Shift+R or Cmd+Shift+R)</li>
                <li>Clear browser cache if needed</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Clerk Domain Setup */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 space-y-4">
          <h2 className="text-purple-400 font-bold text-lg">🌐 Also configure Clerk Domain</h2>
          
          <div className="text-sm text-zinc-300 space-y-2">
            <p>Go to <a href="https://dashboard.clerk.com" target="_blank" rel="noreferrer" className="text-blue-400 hover:underline">Clerk Dashboard</a> → Settings → Domains</p>
            <p>Add your Cloudflare Pages domain:</p>
            <div className="bg-black rounded p-2 font-mono text-yellow-400 text-xs">
              otd-core.pages.dev
            </div>
          </div>
        </div>

        {/* Debug Info */}
        {isDev && (
          <div className="bg-yellow-950 border border-yellow-800 rounded-lg p-4 space-y-2">
            <p className="text-yellow-400 font-bold text-sm">🔍 Debug Info (Development Only)</p>
            <p className="text-yellow-300 text-xs font-mono">Check DevTools Console (F12) for detailed error messages</p>
          </div>
        )}

        {/* Refresh Button */}
        <div className="flex gap-3 justify-center pt-4">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            Refresh Page
          </button>
          <button
            onClick={() => window.location.href = 'https://dashboard.clerk.com'}
            className="px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white font-semibold rounded-lg transition-colors"
          >
            Open Clerk Dashboard
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-zinc-500 text-xs border-t border-zinc-800 pt-4">
          <p>If issues persist, check the browser console (F12) for detailed error messages</p>
        </div>
      </div>
    </div>
  )
}
